import { useMemo, useState, type JSX } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client.js';
import { Avatar, Spinner } from '../ui/common.js';
import { useApp } from '../state/app.js';
import { mmdd } from '../lib/format.js';

const EMP_GRID = '120px 1.2fr 1fr 0.9fr 1.6fr 1.2fr 0.9fr 72px';

function EmployeeDirectory(): JSX.Element {
  const [q, setQ] = useState('');
  const [dept, setDept] = useState('전체');
  const empQ = useQuery({ queryKey: ['employees'], queryFn: () => api.employees() });
  const items = empQ.data?.items ?? [];

  const deptOptions = useMemo(
    () => ['전체', ...Array.from(new Set(items.map((e) => e.dept).filter(Boolean)))],
    [items],
  );
  const filtered = items.filter(
    (e) =>
      (dept === '전체' || e.dept === dept) &&
      (q === '' || e.name.includes(q) || e.empNo.includes(q) || e.upn.includes(q)),
  );

  return (
    <div className="dx-card" style={{ overflow: 'hidden' }}>
      <div style={{ padding: '16px 18px 13px', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', borderBottom: '1px solid #F3F4F6' }}>
        <div style={{ fontSize: 15, fontWeight: 700 }}>전체 임직원</div>
        <span style={{ fontSize: 12, color: '#9CA3AF' }}>총 {items.length}명</span>
        <div style={{ display: 'flex', gap: 8, marginLeft: 'auto', flexWrap: 'wrap' }}>
          <input className="dx-input" style={{ width: 150 }} placeholder="이름·사번·계정 검색" value={q} onChange={(e) => setQ(e.target.value)} />
          <select className="dx-select" value={dept} onChange={(e) => setDept(e.target.value)}>
            {deptOptions.map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
        </div>
      </div>
      {empQ.isLoading ? (
        <div style={{ padding: 40, textAlign: 'center' }}><Spinner /></div>
      ) : empQ.isError ? (
        <div style={{ padding: 32, textAlign: 'center', color: '#9CA3AF', fontSize: 13 }}>임직원 정보를 불러오지 못했습니다.</div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <div style={{ minWidth: 820 }}>
            <div style={{ display: 'grid', gridTemplateColumns: EMP_GRID, background: '#F9FAFB', borderBottom: '1px solid #EDEFF2', fontSize: 12, fontWeight: 600, color: '#6B7280' }}>
              {['사번', '성명', '부서', '직급', '계정(UPN)', '역할', '입사일', '재직'].map((h) => (
                <div key={h} style={{ padding: '10px 12px' }}>{h}</div>
              ))}
            </div>
            {filtered.length === 0 ? (
              <div style={{ padding: 32, textAlign: 'center', color: '#9CA3AF', fontSize: 13 }}>검색 결과가 없습니다.</div>
            ) : filtered.map((e, i) => (
              <div key={e.id} style={{ display: 'grid', gridTemplateColumns: EMP_GRID, borderBottom: '1px solid #F3F4F6', fontSize: 13, alignItems: 'center', background: i % 2 === 0 ? '#fff' : '#FAFBFC' }}>
                <div style={{ padding: '10px 12px', color: '#6B7280' }} className="tnum">{e.empNo}</div>
                <div style={{ padding: '10px 12px', display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Avatar name={e.name} index={i} size={26} upn={e.upn} /><span style={{ fontWeight: 600 }}>{e.name}</span>
                </div>
                <div style={{ padding: '10px 12px', color: '#4B5563' }}>{e.dept}</div>
                <div style={{ padding: '10px 12px', color: '#4B5563' }}>{e.title ?? '—'}</div>
                <div style={{ padding: '10px 12px', color: '#6B7280', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{e.upn}</div>
                <div style={{ padding: '10px 12px', display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                  {e.roles.map((r) => (
                    <span key={r} style={{ fontSize: 11, fontWeight: 600, padding: '2px 7px', borderRadius: 6, background: '#EFF4FE', color: '#1D4ED8' }}>{r}</span>
                  ))}
                </div>
                <div style={{ padding: '10px 12px', color: '#6B7280' }} className="tnum">{e.hireDate ? mmdd(e.hireDate) : '—'}</div>
                <div style={{ padding: '10px 12px' }}>
                  <span style={{ fontSize: 12, fontWeight: 600, padding: '2px 9px', borderRadius: 999, whiteSpace: 'nowrap', display: 'inline-block', ...(e.active ? { background: '#DCFCE7', color: '#15803D' } : { background: '#F3F4F6', color: '#9CA3AF' }) }}>{e.active ? '재직' : '퇴직'}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function Card({ title, desc, children }: { title: string; desc?: string; children: React.ReactNode }): JSX.Element {
  return (
    <div className="dx-card" style={{ padding: 20 }}>
      <div style={{ fontSize: 15, fontWeight: 700, marginBottom: desc ? 4 : 16 }}>{title}</div>
      {desc && <div style={{ fontSize: 12.5, color: '#9CA3AF', marginBottom: 16 }}>{desc}</div>}
      {children}
    </div>
  );
}

function Field({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div>
      <div style={{ fontSize: 12, fontWeight: 600, color: '#6B7280', marginBottom: 6 }}>{label}</div>
      <input className="dx-input" style={{ width: '100%', padding: '9px 11px', fontSize: 14 }} defaultValue={value} />
    </div>
  );
}

function Kv({ k, v }: { k: string; v: string }): JSX.Element {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      <span style={{ fontSize: 13, color: '#4B5563' }}>{k}</span>
      <span style={{ fontSize: 13, fontWeight: 600 }}>{v}</span>
    </div>
  );
}

const divider = <div style={{ height: 1, background: '#F3F4F6' }} />;
const pill = (bg: string, fg: string) => ({ background: bg, color: fg, fontSize: 13, fontWeight: 600, padding: '7px 13px', borderRadius: 999 } as const);

function HolidaysCard(): JSX.Element {
  const { me, flash } = useApp();
  const qc = useQueryClient();
  const canEdit = me.permissions.canManageConfig;
  const [adding, setAdding] = useState(false);
  const [date, setDate] = useState('');
  const [name, setName] = useState('');

  const q = useQuery({ queryKey: ['holidays'], queryFn: () => api.holidays() });
  const items = q.data?.items ?? [];
  const refresh = () => qc.invalidateQueries({ queryKey: ['holidays'] });

  const add = useMutation({
    mutationFn: () => api.createHoliday({ date, name: name.trim() }),
    onSuccess: () => { flash('공휴일이 추가되었습니다'); setAdding(false); setDate(''); setName(''); refresh(); },
    onError: (e) => flash(e instanceof Error ? e.message : '추가 실패'),
  });
  const del = useMutation({
    mutationFn: (id: string) => api.deleteHoliday(id),
    onSuccess: () => { flash('공휴일이 삭제되었습니다'); refresh(); },
    onError: (e) => flash(e instanceof Error ? e.message : '삭제 실패'),
  });
  const canSubmit = /^\d{4}-\d{2}-\d{2}$/.test(date) && name.trim().length > 0;

  return (
    <Card title="공휴일">
      <div style={{ display: 'flex', alignItems: 'center', marginTop: -4, marginBottom: 14 }}>
        {canEdit && (
          <button className="dx-btn dx-btn-ghost" style={{ marginLeft: 'auto', fontSize: 13, padding: '6px 13px' }} onClick={() => setAdding((v) => !v)}>
            {adding ? '닫기' : '+ 추가'}
          </button>
        )}
      </div>
      {adding && canEdit && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 14 }}>
          <input type="date" className="dx-input" value={date} onChange={(e) => setDate(e.target.value)} />
          <input className="dx-input" placeholder="휴일명" value={name} onChange={(e) => setName(e.target.value)} style={{ flex: 1, minWidth: 120 }} />
          <button className="dx-btn dx-btn-primary" style={{ fontSize: 13, padding: '8px 14px' }} disabled={!canSubmit || add.isPending} onClick={() => add.mutate()}>추가</button>
        </div>
      )}
      {q.isLoading ? (
        <Spinner />
      ) : items.length === 0 ? (
        <div style={{ fontSize: 13, color: '#9CA3AF' }}>등록된 공휴일이 없습니다.</div>
      ) : (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {items.map((h) => (
            <span key={h.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, padding: '6px 12px', borderRadius: 7, background: '#F3F4F6', color: '#4B5563' }}>
              <span className="tnum">{mmdd(h.holidayDate)}</span> {h.name}
              {canEdit && (
                <button onClick={() => del.mutate(h.id)} title="삭제" disabled={del.isPending}
                  style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: '#9CA3AF', fontSize: 14, lineHeight: 1, padding: 0 }}>×</button>
              )}
            </span>
          ))}
        </div>
      )}
    </Card>
  );
}

export function Settings(): JSX.Element {
  const cfgQ = useQuery({ queryKey: ['config'], queryFn: () => api.config() });
  const cfg = (cfgQ.data?.config ?? {}) as Record<string, unknown>;
  const workStart = typeof cfg.workStartTime === 'string' ? cfg.workStartTime : '09:00';
  const workEnd = typeof cfg.workEndTime === 'string' ? cfg.workEndTime : '18:00';
  const annual = typeof cfg.annualLeaveDefault === 'number' ? `${cfg.annualLeaveDefault}일 / 년` : '15일 / 년';

  return (
    <div style={{ maxWidth: 980, margin: '0 auto', animation: 'dxfade .3s ease', display: 'flex', flexDirection: 'column', gap: 16 }}>
      <EmployeeDirectory />

      <Card title="근무 시각" desc="표준 출퇴근 시각과 유연근무 허용 범위를 설정합니다.">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px,1fr))', gap: 14 }}>
          <Field label="표준 출근" value={workStart} />
          <Field label="표준 퇴근" value={workEnd} />
          <Field label="유연근무 허용" value="± 1시간" />
        </div>
      </Card>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px,1fr))', gap: 16 }}>
        <Card title="반차 시간대">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 11 }}>
            <Kv k="오전 반차" v="09:00 ~ 13:00" />{divider}
            <Kv k="오후 반차" v="14:00 ~ 18:00" />
          </div>
        </Card>
        <Card title="연차 정책">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 11 }}>
            <Kv k="기본 부여" v={annual} />{divider}
            <Kv k="이월 한도" v="5일" />{divider}
            <Kv k="회계연도 기준" v="1월 1일" />
          </div>
        </Card>
      </div>

      <HolidaysCard />

      <Card title="휴가 결재선" desc="휴가 신청 시 자동으로 적용되는 승인 단계입니다.">
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={pill('#EFF4FE', '#1D4ED8')}>신청자</span>
          <span style={{ color: '#C0C4CB' }}>→</span>
          <span style={pill('#F3F4F6', '#4B5563')}>팀장 (참조)</span>
          <span style={{ color: '#C0C4CB' }}>→</span>
          <span style={pill('#FEF3C7', '#B45309')}>대표이사 (승인)</span>
        </div>
      </Card>

      <Card title="앱 관리" desc="DXTECH Works 앱의 배포·고정·가용성을 관리 센터에서 관리합니다.">
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <a href="https://admin.teams.microsoft.com/policies/manage-apps" target="_blank" rel="noreferrer"
            className="dx-btn dx-btn-ghost" style={{ textDecoration: 'none' }}>Teams 앱 관리 ↗</a>
          <a href="https://admin.cloud.microsoft/#/Settings/IntegratedApps" target="_blank" rel="noreferrer"
            className="dx-btn dx-btn-ghost" style={{ textDecoration: 'none' }}>Outlook·M365 통합 앱 관리 ↗</a>
        </div>
        <div style={{ fontSize: 12, color: '#9CA3AF', marginTop: 10, lineHeight: 1.6 }}>
          · Teams 관리 센터: 앱 가용성·설정 정책(레일 고정) 관리<br />
          · M365 통합 앱: 사용자 지정 앱 업로드·배포·권한 동의 (Outlook 포함)
        </div>
      </Card>
    </div>
  );
}
